import React, { useMemo, useState } from 'react';
import type { TeamMember, TeamMemberCategory, Settings } from '../../types';
import { useCollection } from '../../hooks/useCollection';
import TeamMemberCard from '../../components/TeamMemberCard';
import TeamMemberModal from '../../components/TeamMemberModal';
import { useSEO } from '../../hooks/useSEO';

const CATEGORY_ORDER: TeamMemberCategory[] = ['Architects', 'Professionals', 'Semi-Professionals', 'Technicals', 'Administrative'];

const TeamPage: React.FC<{ settings: Settings }> = ({ settings }) => {
    const { data: teamMembers, isLoading } = useCollection<TeamMember>('teamMembers', { field: 'order', direction: 'asc' });
    const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);

    useSEO({
        title: `Our Team | ${settings?.companyName || 'ARCH.'}`,
        description: `Meet the team of experienced architects, design professionals, and administrative staff at ${settings?.companyName || 'ARCH.'}.`,
        ogImage: settings?.openGraph?.imageUrl
    }, [settings]);

    const groupedMembers = useMemo(() => {
        if (!teamMembers) return {};
        return teamMembers.reduce((acc, member) => {
            const category = member.category || 'Administrative';
            if (!acc[category]) {
                acc[category] = [];
            }
            acc[category].push(member);
            return acc;
        }, {} as Record<string, TeamMember[]>);
    }, [teamMembers]);

    return (
        <>
            {selectedMember && <TeamMemberModal member={selectedMember} onClose={() => setSelectedMember(null)} />}
            <div className="bg-brand-light text-brand-dark pt-32 pb-16">
                <div className="container mx-auto px-6 max-w-6xl">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <h1 className="text-5xl md:text-6xl font-extrabold tracking-tight font-heading">Our Team</h1>
                        <p className="mt-4 text-lg text-gray-600 font-body">
                            We are a collective of passionate architects, designers, and thinkers united by a shared dedication to creating meaningful and enduring spaces.
                        </p>
                    </div>

                    {isLoading && <p className="text-center">Loading team...</p>}
                    
                    {!isLoading && (
                        <div className="space-y-16">
                            {CATEGORY_ORDER.map(category => (
                                groupedMembers[category] && groupedMembers[category].length > 0 && (
                                    <section key={category}>
                                        <h2 className="text-3xl font-bold font-heading text-center mb-12 border-b pb-4">{category}</h2>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
                                            {groupedMembers[category].map(member => (
                                                <TeamMemberCard key={member.id} member={member} onSelect={() => setSelectedMember(member)} />
                                            ))}
                                        </div>
                                    </section>
                                )
                            ))}
                        </div>
                    )}

                    {!isLoading && (!teamMembers || teamMembers.length === 0) && (
                        <p className="text-center text-gray-500 py-12">No team members have been added yet.</p>
                    )}
                </div>
            </div>
        </>
    );
};

export default TeamPage;